import { useEffect, useState } from 'react';
import { Image } from '@/app/_components/common/Image';
import { useInactivityTimer } from '@/app/_lib/hooks/useInactivityTimer';
import { trackGtmEvent } from '@/app/_lib/utils/ndrUtils';
import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import { BasicModal } from '@/app_components/ui/BasicModal';
import { Close } from '@/app_components/ui/primitives/Dialog';
import type { Review } from '@/app_data/mock/ndrData';
import { RatingComponent } from './Rating';

interface PopupProps {
  image: string;
  review: Review;
  queryParams?: string;
}

export const NdrPopup = ({ image, review, queryParams = '' }: PopupProps) => {
  const [open, setOpen] = useState(false);
  const modalContent = (
    <div className='flex flex-col relative lg:flex-row items-stretch m-auto bg-pure rounded-md max-lg:w-[328px] w-[840px] font-graphik-compact text-neutral-900'>
      <div className='w-[420px]'>
        <Image
          alt='popup image'
          className='rounded-t-md lg:rounded-t-none lg:rounded-l-md lg:w-[420px] lg:h-full'
          height={252}
          priority
          src={image}
          width={328}
        />
      </div>
      <div className='flex flex-col gap-8 lg:gap-10 px-6 py-6 lg:py-8'>
        <div className='flex flex-col items-start gap-4'>
          <div className='flex items-center h-[25px] px-2 py-1 rounded-sm bg-blue-100 text-blue-700 font-medium typography-sm'>
            Top rated solution
          </div>
          <div className='flex justify-between items-center self-stretch lg:gap-10'>
            <div className='flex-shrink-0'>
              <Image
                alt={`${review.companyName} logo`}
                className='h-8 w-auto'
                data-cy='ndr-sticky-bar-company-logo'
                fullWidth={false}
                height={28}
                src={review.companyLogos.footerCard || ''}
                width={100}
              />
            </div>
            <RatingComponent
              rating={review.reviewScore}
              reviewLabel={review.reviewLabel}
            />
          </div>
        </div>
        <div className='flex flex-col justify-center items-center gap-6'>
          <div className='flex flex-col gap-2 max-lg:w-[265px]'>
            <div className='text-center font-semibold typography-level-6'>{review.infoTitle}</div>
            <div className='text-center font-normal typography-default'>{review.infoText}</div>
          </div>
          <div className='flex flex-col items-center gap-3'>
            <Link
              className='rounded-full bg-blue-600 text-neutral-50 text-base font-medium leading-[100%] py-4 px-8'
              href={`${review.ctas.sticky.url}?${queryParams}`}
              onClick={() => {
                trackGtmEvent(`${review.ctas.sticky.url}?${queryParams}`);
              }}
            >
              Get Your Free Estimate
            </Link>
            <div className='text-center font-normal typography-sm'>On {review.companyName}'s Website</div>
          </div>
        </div>
      </div>
      <Close
        asChild
        className='absolute right-2 lg:right-4 top-2 lg:top-4 bg-pure rounded-full'
      >
        <Icon icon='X' />
      </Close>
    </div>
  );

  const inactivityDelay = 30000;
  const showOnce = true;

  // Use inactivity timer conditionally
  const { isInactive: shouldShowFromInactivity, resetInactiveState } = useInactivityTimer({
    delay: inactivityDelay,
    showOnce,
  });

  // Handle inactivity trigger
  useEffect(() => {
    if (shouldShowFromInactivity && !open) {
      setOpen(true);
    }
  }, [shouldShowFromInactivity, open]);

  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetInactiveState();
    }
  };

  return (
    <BasicModal
      content={modalContent}
      onOpenChange={onOpenChange}
      openOverride={open}
      trigger={undefined}
    />
  );
};
